import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CalendarLayout, SupportEmailText } from '../components';
import { SUPPORT_EMAIL } from '../constants';
import { useDraftTesterOptOut } from '../hooks';
import * as S from './styles';

const STATUS = {
  idle: 'idle',
  sending: 'sending',
  success: 'success',
  invalid: 'invalid',
  failed: 'failed',
};

const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function CalendarDraftTesters() {
  const [t] = useTranslation('calendar');
  const { requestOptOut } = useDraftTesterOptOut();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(STATUS.idle);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const nextSteps = t('draft-testers.next-steps.items', {
    returnObjects: true,
  });

  const handleSubmit = async event => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!isValidEmail(trimmedEmail)) {
      setStatus(STATUS.invalid);
      return;
    }

    setStatus(STATUS.sending);

    try {
      await requestOptOut({
        email: trimmedEmail,
        message: trimmedMessage,
      });

      setSubmittedEmail(trimmedEmail);
      setEmail('');
      setMessage('');
      setStatus(STATUS.success);
    } catch {
      setSubmittedEmail(trimmedEmail);
      setStatus(STATUS.failed);
    }
  };

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    t('draft-testers.fallback.subject'),
  )}&body=${encodeURIComponent(
    t('draft-testers.fallback.body', { email: submittedEmail }),
  )}`;

  return (
    <CalendarLayout>
      <S.Title>{t('draft-testers.title')}</S.Title>
      <S.Paragraph>
        <Trans
          i18nKey="draft-testers.description"
          ns="calendar"
          components={{ strong: <strong /> }}
        />
      </S.Paragraph>
      <S.Paragraph>{t('draft-testers.not-notified')}</S.Paragraph>

      <S.FormCard>
        <S.SectionHeading>{t('draft-testers.form.heading')}</S.SectionHeading>
        <S.Paragraph>{t('draft-testers.form.description')}</S.Paragraph>

        <S.Form onSubmit={handleSubmit} noValidate>
          <S.Field>
            <S.FieldLabel htmlFor="draft-tester-email">
              {t('draft-testers.form.email-label')}
            </S.FieldLabel>
            <S.TextInput
              id="draft-tester-email"
              name="email"
              type="email"
              autoComplete="email"
              spellCheck="false"
              placeholder={t('draft-testers.form.email-placeholder')}
              value={email}
              onChange={event => setEmail(event.target.value)}
            />
            <S.FieldHint>{t('draft-testers.form.email-hint')}</S.FieldHint>
          </S.Field>

          <S.Field>
            <S.FieldLabel htmlFor="draft-tester-message">
              {t('draft-testers.form.message-label')}
            </S.FieldLabel>
            <S.TextArea
              id="draft-tester-message"
              name="message"
              maxLength={2000}
              placeholder={t('draft-testers.form.message-placeholder')}
              value={message}
              onChange={event => setMessage(event.target.value)}
            />
          </S.Field>

          <S.SubmitButton
            type="submit"
            variant="contained"
            color="porterBlue"
            centered
            loading={status === STATUS.sending}
            disabled={status === STATUS.sending}
          >
            {t('draft-testers.form.submit')}
          </S.SubmitButton>

          {status === STATUS.invalid && (
            <S.StatusMessage role="status" $variant="danger">
              {t('draft-testers.form.invalid-email')}
            </S.StatusMessage>
          )}

          {status === STATUS.success && (
            <S.StatusMessage role="status" $variant="success">
              {t('draft-testers.form.success', { email: submittedEmail })}
            </S.StatusMessage>
          )}

          {status === STATUS.failed && (
            <>
              <S.StatusMessage role="status" $variant="danger">
                {t('draft-testers.form.failed')}
              </S.StatusMessage>
              <S.Paragraph>
                <SupportEmailText
                  i18nKey="draft-testers.fallback.instruction"
                  email={SUPPORT_EMAIL}
                  href={mailtoHref}
                />
              </S.Paragraph>
            </>
          )}
        </S.Form>
      </S.FormCard>

      <S.SectionHeading>
        {t('draft-testers.next-steps.heading')}
      </S.SectionHeading>
      <S.BulletList>
        {Array.isArray(nextSteps) &&
          nextSteps.map(step => <li key={step}>{step}</li>)}
      </S.BulletList>

      <S.Divider />

      <S.Paragraph>{t('draft-testers.data-use')}</S.Paragraph>

      <S.Paragraph>
        <SupportEmailText
          i18nKey="draft-testers.support"
          email={SUPPORT_EMAIL}
        />
      </S.Paragraph>

      <S.LegalNotice $align="left">
        {t('draft-testers.trademarks')}
      </S.LegalNotice>
    </CalendarLayout>
  );
}
